import { useEffect, useState, useCallback } from "react";
import { api, getWalletId } from "@/lib/api";
import { toast } from "sonner";
import SpurrChart from "@/sections/trading/SpurrChart";
import TradeTerminal from "@/sections/trading/TradeTerminal";
import TradeHistory from "@/sections/trading/TradeHistory";
import { fmtSPURR } from "@/sections/trading/fmt";

export default function MockTrading() {
  const walletId = getWalletId();
  const [price, setPrice] = useState(null);
  const [chart, setChart] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [trades, setTrades] = useState([]);
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("100");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [p, c, w, h] = await Promise.all([
        api.get("/emg/price"),
        api.get("/emg/chart?hours=24"),
        api.get(`/wallet/${walletId}`),
        api.get(`/trade/history/${walletId}`),
      ]);
      setPrice(p.data);
      setChart(
        c.data.points.map((pt) => ({
          t: new Date(pt.t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          p: pt.p,
        }))
      );
      setWallet(w.data);
      setTrades(h.data.trades);
    } catch {
      // Intentional: polls every 15s — transient failures recover on the next tick.
    }
  }, [walletId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid USD amount");
    setBusy(true);
    try {
      const { data } = await api.post("/trade", { wallet_id: walletId, side, amount_usd: amt });
      toast.success(`${side.toUpperCase()} ${fmtSPURR(data.trade.emg_amount)} SPURR @ $${data.trade.price.toFixed(8)}`);
      setAmount("100");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trade failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try {
      await api.post(`/wallet/${walletId}/reset`);
      toast.success("Paper wallet reset to $10,000");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed — try again");
    }
  };

  const portfolio =
    (wallet?.usd_balance || 0) + (wallet?.emg_balance || 0) * (price?.price || 0);

  return (
    <section id="trade" className="border-t border-[#3a3a3a]" data-testid="section-trade">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              04 // Paper Trading
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter">
              Trade SPURR.
              <br />
              No money, all signal.
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-4 self-end text-[#e2e2e2] text-sm">
            Every browser gets a fresh $10,000 paper wallet. Practice the loop your future
            community will go through.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <SpurrChart price={price} chart={chart} />
          <TradeTerminal
            side={side}
            setSide={setSide}
            amount={amount}
            setAmount={setAmount}
            price={price}
            busy={busy}
            onSubmit={submit}
            wallet={wallet}
            portfolio={portfolio}
            onReset={reset}
          />
          <TradeHistory trades={trades} />
        </div>
      </div>
    </section>
  );
}
